/**
 * สูตรระดับบรรทัดและระดับบิล
 *
 * นี่คือจุดเดียวในระบบที่คำนวณยอดบิล — เว็บ POS หลังบ้าน และรายงาน
 * เรียกฟังก์ชันชุดเดียวกันนี้ทั้งหมด
 *
 * บั๊กเดิมที่โครงนี้ปิดตายไปแล้ว
 *   · JS คิด qty×price ดิบ แต่ SQL เขียน ceil() → 488 บิลยอดไม่ตรง 46,863 บาท
 *   · ส่วนลด % คำนวณได้ 0 เสมอ เพราะลูปเช็คตัวแปรผิดตัว → 111 บรรทัด
 *   · ส่วนลดถูกเก็บเป็นบรรทัดสินค้าปลอม ทำให้ยอดรวมและรายงานสินค้าขายดีเพี้ยน
 */

import { assertSatang, ceilToBaht, percentOf, sumSatang } from './money.ts';
import type {
  BillTotals,
  BillType,
  Discount,
  ProductPricing,
  SaleLine,
} from './types.ts';

/**
 * ราคาต่อหน่วยตามประเภทบิล
 *
 * ทานที่ร้านและสั่งกลับบ้านใช้ราคาเดียวกัน มีแต่เดลิเวอรี่ที่ใช้ราคาที่สอง
 * (ยืนยันจากข้อมูลจริง: delivery_channel.price_level = 2 ทุกเจ้า)
 */
export function unitPriceFor(
  product: ProductPricing,
  billType: BillType,
): number {
  return billType === 'delivery'
    ? product.priceDeliverySatang
    : product.priceDineInSatang;
}

/** ยอดของบรรทัดเดียว — ตรงกับ GENERATED COLUMN ในฐานข้อมูลเป๊ะ */
export function lineTotal(line: SaleLine): number {
  if (!Number.isInteger(line.qty) || line.qty <= 0) {
    throw new Error(`qty ต้องเป็นจำนวนเต็มบวก แต่ได้ ${line.qty}`);
  }
  assertSatang(line.unitPriceSatang, 'unitPriceSatang');
  return line.qty * line.unitPriceSatang;
}

/** ยอดรวมสินค้า — นับเฉพาะบรรทัดที่ยังไม่ถูกยกเลิก */
export function computeSubtotal(lines: readonly SaleLine[]): number {
  return sumSatang(
    lines.filter((l) => !l.voided).map(lineTotal),
    'lineTotal',
  );
}

/**
 * ส่วนลดหนึ่งรายการ
 *
 * ★ ส่วนลดมีที่ระดับบิลเท่านั้น ไม่มีส่วนลดระดับรายการสินค้า ★
 * ★ ส่วนลดที่มีเศษสตางค์ ปัดขึ้นเป็นบาทเต็มเสมอ ★
 *
 *   150 บาท ลด 5%   → 7.50  → หัก 8 บาท
 *   1,205 บาท ลด 5% → 60.25 → หัก 61 บาท
 *   100 บาท ลด 10%  → 10.00 → หัก 10 บาท (ลงตัวแล้ว ห้ามปัดเพิ่ม)
 */
export function computeDiscount(
  subtotalSatang: number,
  discount: Discount,
): number {
  assertSatang(subtotalSatang, 'subtotalSatang');

  if (discount.kind === 'fixed') {
    if (discount.amountSatang === undefined) {
      throw new Error('ส่วนลดแบบจำนวนเงินต้องระบุ amountSatang');
    }
    assertSatang(discount.amountSatang, 'discount.amountSatang');
    if (discount.amountSatang <= 0) {
      throw new Error('ส่วนลดต้องมากกว่า 0');
    }
    return discount.amountSatang;
  }

  if (discount.percent === undefined) {
    throw new Error(`ส่วนลดแบบ ${discount.kind} ต้องระบุ percent`);
  }
  return ceilToBaht(percentOf(subtotalSatang, discount.percent));
}

/**
 * ยอดทั้งบิล
 *
 * ส่วนลดทุกตัวคิดจาก subtotal เดียวกัน แล้วเอามาบวกกัน — ไม่หักซ้อนเป็นชั้น
 * ในทางปฏิบัติบิลหนึ่งมีส่วนลดตัวเดียว แต่รองรับหลายตัวไว้เผื่อสมาชิกมาเจอโปรพอดี
 */
export function computeBillTotals(
  lines: readonly SaleLine[],
  discounts: readonly Discount[] = [],
): BillTotals {
  const subtotalSatang = computeSubtotal(lines);

  const discountSatang = sumSatang(
    discounts.map((d) => computeDiscount(subtotalSatang, d)),
    'discount',
  );

  if (discountSatang > subtotalSatang) {
    throw new Error(
      `ส่วนลด (${discountSatang}) มากกว่ายอดรวมสินค้า (${subtotalSatang}) — ยอดบิลติดลบไม่ได้`,
    );
  }

  return {
    subtotalSatang,
    discountSatang,
    totalSatang: subtotalSatang - discountSatang,
  };
}

/**
 * คิดราคาใหม่ทุกบรรทัดเมื่อเปลี่ยนประเภทบิล
 *
 * เปลี่ยนจากทานที่ร้านเป็นเดลิเวอรี่ ราคาทุกบรรทัดต้องเปลี่ยนตาม
 * บรรทัดที่ยกเลิกไปแล้วไม่แตะ — ราคาตอนนั้นคือหลักฐานว่าเคยขายเท่าไร
 */
export function repriceLines(
  lines: readonly SaleLine[],
  products: ReadonlyMap<number, ProductPricing>,
  billType: BillType,
): SaleLine[] {
  return lines.map((line) => {
    if (line.voided) return line;

    const product = products.get(line.productId);
    if (!product) {
      throw new Error(`ไม่พบสินค้ารหัส ${line.productId} ตอนคิดราคาใหม่`);
    }

    return { ...line, unitPriceSatang: unitPriceFor(product, billType) };
  });
}

/**
 * รายการที่ต้องส่งเข้าครัวสำหรับรอบล่าสุด
 *
 * กฎที่ยกมาจากระบบเดิม: เพิ่มออเดอร์ = รอบใหม่ · ครัวพิมพ์เฉพาะรอบล่าสุด
 * ถ้าพิมพ์ทั้งบิลทุกครั้ง ครัวจะทำอาหารซ้ำทันทีในชั่วโมงแรก
 */
export function linesForKitchenRound<T extends { orderRound: number; voided?: boolean }>(
  lines: readonly T[],
  orderRound: number,
): T[] {
  return lines.filter((l) => l.orderRound === orderRound && !l.voided);
}
